// EmanetX web-musteri (qr.emanetx.com)
// QR misafir kiralama + Akbank odeme sonuc + yasal sayfalar.
// Path-based routing — manual, ek paket yok.
//   /                       → Giris: nokta kodu input
//   /dolap/:noktaKodu       → Kiralama iskelet (akis sonra doldurulacak)
//   /odeme/sonuc/:token     → Akbank callback sonrasi polling sonuc ekrani
//   /gizlilik-politikasi    → Gizlilik politikasi (mobile WebView modal'i de buraya bakar)
//   /sozlesme · /cerez · /kvkk · /yasal → Yasal sayfalar

import { useEffect, useState } from 'react';
import { YasalRota, EPOSTA } from './yasal.js';
import { YardimRota } from './yardim.js';

const API_URL = import.meta.env.VITE_API_TABAN_URL ?? 'https://api.emanetx.com';

export function App() {
  const yol = window.location.pathname;

  if (yol.startsWith('/odeme/sonuc/')) {
    const token = yol.split('/').pop() ?? '';
    return <OdemeSonuc token={token} />;
  }

  if (yol.startsWith('/dolap/')) {
    const noktaKodu = yol.split('/').pop() ?? '';
    return <NoktaSayfasi kod={noktaKodu} />;
  }

  const yardim = YardimRota({ yol });
  if (yardim) return <>{yardim}</>;

  const yasal = YasalRota({ yol });
  if (yasal) return <>{yasal}</>;

  return <GirisSayfasi />;
}

function GirisSayfasi() {
  const [kod, setKod] = useState('');

  const git = (e: React.FormEvent) => {
    e.preventDefault();
    const temiz = kod.trim().toUpperCase();
    if (!temiz) return;
    window.location.href = `/dolap/${encodeURIComponent(temiz)}`;
  };

  return (
    <main className="flex min-h-screen items-center justify-center bg-zinc-50 px-6">
      <div className="w-full max-w-md">
        <header className="text-center">
          <h1 className="text-3xl font-extrabold tracking-tight text-zinc-900">
            emanet<span className="text-amber-500">x</span>
          </h1>
          <p className="mt-1 text-sm text-zinc-500">Misafir Kiralama</p>
        </header>

        <section className="mt-10 rounded-3xl border border-zinc-200 bg-white p-8 shadow-sm">
          <h2 className="text-xl font-extrabold text-zinc-900">Dolap kodunu gir</h2>
          <p className="mt-1 text-sm text-zinc-500">
            Dolabın yanındaki QR kodu taradığında bu sayfaya gelirsin. Manuel girmek için aşağıya
            yaz.
          </p>

          <form onSubmit={git} className="mt-6 space-y-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-zinc-600">
                Nokta Kodu
              </label>
              <input
                type="text"
                value={kod}
                onChange={(e) => setKod(e.target.value)}
                required
                placeholder="ABC123"
                autoComplete="off"
                autoCapitalize="characters"
                className="mt-1 w-full rounded-xl border border-zinc-200 bg-white px-4 py-3 text-center font-mono text-xl font-extrabold tracking-widest outline-none transition focus:border-amber-400"
              />
            </div>

            <button
              type="submit"
              className="w-full rounded-xl bg-amber-400 px-6 py-3 text-sm font-extrabold text-zinc-900 transition hover:bg-amber-500 active:scale-95"
            >
              Devam Et
            </button>
          </form>
        </section>

        <p className="mt-6 text-center text-xs text-zinc-400">
          © {new Date().getFullYear()} EmanetX ·{' '}
          <a className="hover:text-zinc-600" href={`mailto:${EPOSTA}`}>
            {EPOSTA}
          </a>
          <span className="px-1">·</span>
          <a className="hover:text-zinc-600" href="/yasal">
            Yasal
          </a>
          <span className="px-1">·</span>
          <a className="hover:text-zinc-600" href="/kvkk">
            KVKK
          </a>
        </p>
      </div>
    </main>
  );
}

function NoktaSayfasi({ kod }: { kod: string }) {
  return (
    <main className="flex min-h-screen items-center justify-center bg-zinc-50 px-6">
      <div className="w-full max-w-md text-center">
        <h1 className="text-2xl font-extrabold tracking-tight text-zinc-900">
          Dolap <span className="font-mono text-amber-500">{kod}</span>
        </h1>
        <p className="mt-3 text-sm text-zinc-600">
          QR misafir kiralama akışı yakında. Şimdilik uygulamayı indirip telefonla devam
          edebilirsin.
        </p>
        <a
          href="/"
          className="mt-8 inline-flex items-center justify-center rounded-xl border border-zinc-200 bg-white px-5 py-2 text-sm font-bold text-zinc-700 hover:bg-zinc-100"
        >
          ← Geri
        </a>
      </div>
    </main>
  );
}

// === Odeme sonuc sayfasi — Akbank callback sonrasi ===
// Backend /odeme/callback/akbank/:token → 303 redirect /odeme/sonuc/:token
// Bu sayfa /odeme/durum/:token polling yapar — mobile WebView veya web tarayicidan acilabilir.

type DurumKodu = 'BEKLEMEDE' | 'ISLENIYOR' | 'BASARILI' | 'BASARISIZ' | 'IPTAL' | 'SURESI_DOLDU';

interface DurumYanit {
  token: string;
  durum: DurumKodu;
  amac: 'KIRALAMA' | 'CUZDAN_YUKLE';
  tutar: string;
  paraBirimi: string;
  bankaIslemId: string | null;
  hataMesaji: string | null;
  olusturmaTarihi: string;
}

function OdemeSonuc({ token }: { token: string }) {
  const [durum, setDurum] = useState<DurumYanit | null>(null);
  const [bitti, setBitti] = useState(false);

  useEffect(() => {
    if (!token) return;
    let aktif = true;
    const tikla = async () => {
      try {
        const yanit = await fetch(`${API_URL}/odeme/durum/${token}`);
        if (!yanit.ok) throw new Error('durum-getirilemedi');
        const veri = (await yanit.json()) as DurumYanit;
        if (!aktif) return;
        setDurum(veri);
        if (
          veri.durum === 'BASARILI' ||
          veri.durum === 'BASARISIZ' ||
          veri.durum === 'IPTAL' ||
          veri.durum === 'SURESI_DOLDU'
        ) {
          setBitti(true);
        }
      } catch {
        // sessiz, bir sonraki tick'te yeniden dene
      }
    };
    void tikla();
    const id = setInterval(() => {
      if (!aktif || bitti) {
        clearInterval(id);
        return;
      }
      void tikla();
    }, 2000);
    return () => {
      aktif = false;
      clearInterval(id);
    };
  }, [token, bitti]);

  if (!token) {
    return <SonucMesaj baslik="Geçersiz bağlantı" alt="Ödeme tokenı eksik." renk="kirmizi" />;
  }

  const terminal =
    durum &&
    (durum.durum === 'BASARILI' ||
      durum.durum === 'BASARISIZ' ||
      durum.durum === 'IPTAL' ||
      durum.durum === 'SURESI_DOLDU');

  if (!terminal) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-zinc-50 px-6">
        <div className="max-w-md text-center">
          <div className="mx-auto h-16 w-16 animate-spin rounded-full border-4 border-zinc-200 border-t-amber-500" />
          <h2 className="mt-6 text-2xl font-extrabold text-zinc-900">Ödeme işleniyor...</h2>
          <p className="mt-2 text-sm text-zinc-600">
            Bankadan dönüş bekleniyor. Bu pencereyi kapatma.
          </p>
          {durum && <p className="mt-4 font-mono text-xs text-zinc-400">durum: {durum.durum}</p>}
        </div>
      </main>
    );
  }

  if (durum.durum === 'BASARILI') {
    return (
      <main className="flex min-h-screen items-center justify-center bg-zinc-50 px-6">
        <div className="max-w-md rounded-3xl border border-emerald-200 bg-white p-8 text-center shadow-lg">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 text-3xl">
            ✓
          </div>
          <h2 className="mt-6 text-2xl font-extrabold text-zinc-900">Ödeme Başarılı</h2>
          <p className="mt-2 text-sm text-zinc-600">
            {durum.amac === 'CUZDAN_YUKLE'
              ? `${durum.tutar} ${durum.paraBirimi} cüzdanına yüklendi.`
              : `${durum.tutar} ${durum.paraBirimi} alındı.`}
          </p>
          {durum.bankaIslemId && (
            <p className="mt-3 font-mono text-xs text-zinc-400">İşlem #{durum.bankaIslemId}</p>
          )}
          <p className="mt-6 text-xs text-zinc-500">
            Bu pencereyi kapatabilirsin. Uygulama otomatik devam eder.
          </p>
        </div>
      </main>
    );
  }

  const baslik =
    durum.durum === 'IPTAL'
      ? 'Ödeme İptal Edildi'
      : durum.durum === 'SURESI_DOLDU'
        ? 'Ödeme Süresi Doldu'
        : 'Ödeme Başarısız';
  return (
    <SonucMesaj
      baslik={baslik}
      alt={durum.hataMesaji ?? 'Lütfen kartını kontrol edip tekrar dene.'}
      renk="kirmizi"
    />
  );
}

function SonucMesaj({
  baslik,
  alt,
  renk,
}: {
  baslik: string;
  alt: string;
  renk: 'yesil' | 'kirmizi';
}) {
  const cerceveSinif =
    renk === 'yesil' ? 'border-emerald-200 bg-emerald-50' : 'border-red-200 bg-red-50';
  const ikon = renk === 'yesil' ? '✓' : '✗';
  const ikonBg = renk === 'yesil' ? 'bg-emerald-100' : 'bg-red-100';
  return (
    <main className="flex min-h-screen items-center justify-center bg-zinc-50 px-6">
      <div
        className={`max-w-md rounded-3xl border bg-white p-8 text-center shadow-lg ${cerceveSinif}`}
      >
        <div
          className={`mx-auto flex h-16 w-16 items-center justify-center rounded-full text-3xl ${ikonBg}`}
        >
          {ikon}
        </div>
        <h2 className="mt-6 text-2xl font-extrabold text-zinc-900">{baslik}</h2>
        <p className="mt-2 text-sm text-zinc-600">{alt}</p>
        <p className="mt-6 text-xs text-zinc-500">Bu pencereyi kapatabilirsin.</p>
      </div>
    </main>
  );
}
